import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { profileData } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      console.log('Contact form submitted successfully:', response.data);
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: ''
        });
      }, 5000);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setIsSubmitting(false);
      setError('Failed to send message. Please try again or contact us directly at ' + profileData.email);
    }
  };

  return (
    <div className="page-container">
      {/* Contact Header */}
      <section className="contact-hero">
        <h1 className="display-huge">Get in Touch</h1>
        <p className="body-large contact-subtitle">
          Let's discuss how Videre Scire can guide your organization through complex digital transformations.
        </p>
      </section>

      <div className="contact-container">
        {/* Contact Information */}
        <div className="contact-info-section">
          <h2 className="heading-2">Contact Information</h2>
          <p className="body-medium contact-info-text">
            Reach out directly or fill out the form, and we'll get back to you promptly.
          </p>
          
          <div className="contact-details">
            <a href={`mailto:${profileData.email}`} className="contact-detail-item">
              <div className="contact-detail-icon">
                <Mail size={24} />
              </div>
              <div>
                <p className="body-small text-muted">Email</p>
                <p className="body-medium">{profileData.email}</p>
              </div>
            </a>

            <a href={`tel:${profileData.phone}`} className="contact-detail-item">
              <div className="contact-detail-icon">
                <Phone size={24} />
              </div>
              <div>
                <p className="body-small text-muted">Phone</p>
                <p className="body-medium">{profileData.phone}</p>
              </div>
            </a>
          </div>
        </div>

        {/* Contact Form */}
        <div className="contact-form-section">
          {isSubmitted ? (
            <div className="success-message">
              <CheckCircle size={48} />
              <h3 className="heading-2">Message Sent Successfully!</h3>
              <p className="body-medium">Thank you for reaching out. We'll get back to you soon at {formData.email}</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <AlertCircle size={48} />
              <h3 className="heading-2">Error Sending Message</h3>
              <p className="body-medium">{error}</p>
              <button 
                onClick={() => setError(null)} 
                className="btn-secondary"
                style={{ marginTop: '20px' }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Your company name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="How can we help?"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="form-textarea"
                  placeholder="Tell us about your needs..."
                />
              </div>

              <button 
                type="submit" 
                className="btn-primary submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send size={20} />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
